import React, { useState } from 'react';
import { useTimeTracking } from '../context/TimeTrackingContext';
import { useProjects } from '../context/ProjectContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ResourceName } from '../types/timeTracking';
import { PlusIcon, ClockIcon, TrashIcon, CalendarIcon, FolderIcon, UserIcon, FilterIcon, XIcon } from 'lucide-react';
const RESOURCES: ResourceName[] = ['Sergey Kleymuk', 'Sasha Oleksandr', 'Dmitry Syrov', 'David Nssien'];
export function CostTracking() {
  const {
    selectedCompany
  } = useData();
  const {
    getTimeEntriesByCompanyId,
    addTimeEntry,
    deleteTimeEntry
  } = useTimeTracking();
  const {
    getProjectsByCompanyId
  } = useProjects();
  const [newEntry, setNewEntry] = useState({
    project_id: '',
    resource_name: '' as ResourceName | '',
    hours: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    project_id: '',
    resource_name: '',
    start_date: '',
    end_date: ''
  });
  const timeEntries = selectedCompany ? getTimeEntriesByCompanyId(selectedCompany.id) : [];
  const projects = selectedCompany ? getProjectsByCompanyId(selectedCompany.id) : [];
  // Apply filters
  const filteredEntries = timeEntries.filter(entry => {
    if (filters.project_id && entry.project_id !== filters.project_id) return false;
    if (filters.resource_name && entry.resource_name !== filters.resource_name) return false;
    const entryDate = new Date(entry.date);
    if (filters.start_date) {
      const startDate = new Date(filters.start_date);
      if (entryDate < startDate) return false;
    }
    if (filters.end_date) {
      const endDate = new Date(filters.end_date);
      if (entryDate > endDate) return false;
    }
    return true;
  });
  const hasActiveFilters = filters.project_id || filters.resource_name || filters.start_date || filters.end_date;
  const clearFilters = () => {
    setFilters({
      project_id: '',
      resource_name: '',
      start_date: '',
      end_date: ''
    });
  };
  const handleAddEntry = () => {
    if (!newEntry.project_id || !newEntry.resource_name || !newEntry.hours || !selectedCompany) {
      alert('Please fill in all required fields (Project, Resource, Hours)');
      return;
    }
    const hours = parseFloat(newEntry.hours);
    if (isNaN(hours) || hours <= 0) {
      alert('Please enter a valid number of hours');
      return;
    }
    // Round to nearest 0.25
    const roundedHours = Math.round(hours * 4) / 4;
    addTimeEntry({
      company_id: selectedCompany.id,
      project_id: newEntry.project_id,
      resource_name: newEntry.resource_name as ResourceName,
      hours: roundedHours,
      notes: newEntry.notes,
      date: new Date(newEntry.date)
    });
    // Reset form
    setNewEntry({
      project_id: '',
      resource_name: '' as ResourceName | '',
      hours: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
  };
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.title || 'Unknown Project';
  };
  const getTotalHours = (entries = timeEntries) => {
    return entries.reduce((sum, entry) => sum + entry.hours, 0);
  };
  const getTotalHoursByResource = (entries = timeEntries) => {
    const totals: Record<string, number> = {};
    entries.forEach(entry => {
      totals[entry.resource_name] = (totals[entry.resource_name] || 0) + entry.hours;
    });
    return totals;
  };
  const getTotalHoursByProject = (entries = timeEntries) => {
    const totals: Record<string, number> = {};
    entries.forEach(entry => {
      totals[entry.project_id] = (totals[entry.project_id] || 0) + entry.hours;
    });
    return totals;
  };
  const resourceTotals = getTotalHoursByResource(filteredEntries);
  const projectTotals = getTotalHoursByProject(filteredEntries);
  return <div className="max-w-6xl mx-auto space-y-6" data-id="element-816">
      <div className="flex items-center justify-between" data-id="element-817">
        <div data-id="element-818">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-819">Cost Flow</h1>
          <p className="text-sm text-gray-600" data-id="element-820">
            {hasActiveFilters ? <>
                {filteredEntries.length} of {timeEntries.length} entries •{' '}
                {getTotalHours(filteredEntries).toFixed(2)} hours
              </> : <>
                {timeEntries.length} time entries • {getTotalHours().toFixed(2)}{' '}
                total hours
              </>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-821">
        <div className="flex items-center justify-between mb-4" data-id="element-822">
          <div className="flex items-center gap-2" data-id="element-823">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-824" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-825">Filters</h2>
          </div>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} leftIcon={<XIcon className="w-4 h-4" data-id="element-827" />} data-id="element-826">
              Clear Filters
            </Button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-828">
          <div data-id="element-829">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-830">
              Project
            </label>
            <select value={filters.project_id} onChange={e => setFilters({
            ...filters,
            project_id: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-831">
              <option value="" data-id="element-832">All Projects</option>
              {projects.map(project => <option key={project.id} value={project.id} data-id="element-833">
                  {project.title}
                </option>)}
            </select>
          </div>

          <div data-id="element-834">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-835">
              Resource
            </label>
            <select value={filters.resource_name} onChange={e => setFilters({
            ...filters,
            resource_name: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-836">
              <option value="" data-id="element-837">All Resources</option>
              {RESOURCES.map(resource => <option key={resource} value={resource} data-id="element-838">
                  {resource}
                </option>)}
            </select>
          </div>

          <div data-id="element-839">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-840">
              Start Date
            </label>
            <input type="date" value={filters.start_date} onChange={e => setFilters({
            ...filters,
            start_date: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-841" />
          </div>

          <div data-id="element-842">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-843">
              End Date
            </label>
            <input type="date" value={filters.end_date} onChange={e => setFilters({
            ...filters,
            end_date: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-844" />
          </div>
        </div>
      </Card>

      {/* Filtered Summary */}
      {hasActiveFilters && <Card data-id="element-845">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-846">
            Filtered Summary
          </h2>
          <div className="space-y-4" data-id="element-847">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg" data-id="element-848">
              <div className="flex items-center gap-3" data-id="element-849">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-850">
                  <ClockIcon className="w-5 h-5 text-blue-600" data-id="element-851" />
                </div>
                <div data-id="element-852">
                  <p className="text-sm text-gray-600" data-id="element-853">Total Hours</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-854">
                    {getTotalHours(filteredEntries).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-855">
                <p className="text-sm text-gray-600" data-id="element-856">Entries</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-857">
                  {filteredEntries.length}
                </p>
              </div>
            </div>

            {/* Filtered Entries Details */}
            {filteredEntries.length > 0 && <div className="space-y-2" data-id="element-858">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-859">Entries</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto" data-id="element-860">
                  {filteredEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(entry => <div key={entry.id} className="p-3 bg-gray-50 rounded-lg" data-id="element-861">
                        <div className="flex items-start justify-between mb-2" data-id="element-862">
                          <div className="flex-1 min-w-0" data-id="element-863">
                            <div className="flex items-center gap-2 mb-1" data-id="element-864">
                              <CalendarIcon className="w-3 h-3 text-gray-400" data-id="element-865" />
                              <span className="text-sm font-medium text-gray-900" data-id="element-866">
                                {new Date(entry.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-1" data-id="element-867">
                              <FolderIcon className="w-3 h-3 text-gray-400" data-id="element-868" />
                              <span className="text-xs text-gray-600" data-id="element-869">
                                {getProjectName(entry.project_id)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-870">
                              <UserIcon className="w-3 h-3 text-gray-400" data-id="element-871" />
                              <span className="text-xs text-gray-600" data-id="element-872">
                                {entry.resource_name}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-3" data-id="element-873">
                            <span className="text-lg font-bold text-gray-900" data-id="element-874">
                              {entry.hours.toFixed(2)}h
                            </span>
                          </div>
                        </div>
                        {entry.notes && <p className="text-xs text-gray-600 mt-2 pl-5" data-id="element-875">
                            {entry.notes}
                          </p>}
                      </div>)}
                </div>
              </div>}
          </div>
        </Card>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-876">
        <Card data-id="element-877">
          <div className="flex items-center justify-between" data-id="element-878">
            <div data-id="element-879">
              <p className="text-sm text-gray-600 mb-1" data-id="element-880">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-881">
                {getTotalHours(filteredEntries).toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-882">
              <ClockIcon className="w-6 h-6 text-blue-600" data-id="element-883" />
            </div>
          </div>
        </Card>

        <Card data-id="element-884">
          <div className="flex items-center justify-between" data-id="element-885">
            <div data-id="element-886">
              <p className="text-sm text-gray-600 mb-1" data-id="element-887">Active Projects</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-888">
                {Object.keys(projectTotals).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center" data-id="element-889">
              <FolderIcon className="w-6 h-6 text-orange-600" data-id="element-890" />
            </div>
          </div>
        </Card>

        <Card data-id="element-891">
          <div className="flex items-center justify-between" data-id="element-892">
            <div data-id="element-893">
              <p className="text-sm text-gray-600 mb-1" data-id="element-894">Resources</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-895">
                {Object.keys(resourceTotals).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-896">
              <UserIcon className="w-6 h-6 text-purple-600" data-id="element-897" />
            </div>
          </div>
        </Card>

        <Card data-id="element-898">
          <div className="flex items-center justify-between" data-id="element-899">
            <div data-id="element-900">
              <p className="text-sm text-gray-600 mb-1" data-id="element-901">Avg Hours/Entry</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-902">
                {filteredEntries.length > 0 ? (getTotalHours(filteredEntries) / filteredEntries.length).toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-903">
              <CalendarIcon className="w-6 h-6 text-green-600" data-id="element-904" />
            </div>
          </div>
        </Card>
      </div>

      {/* Add Time Entry */}
      <Card data-id="element-905">
        <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-906">Log Time</h2>
        <div className="space-y-4" data-id="element-907">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-908">
            <div data-id="element-909">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-910">
                Project <span className="text-red-600" data-id="element-911">*</span>
              </label>
              <select value={newEntry.project_id} onChange={e => setNewEntry({
              ...newEntry,
              project_id: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-912">
                <option value="" data-id="element-913">Select Project</option>
                {projects.map(project => <option key={project.id} value={project.id} data-id="element-914">
                    {project.title}
                  </option>)}
              </select>
            </div>

            <div data-id="element-915">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-916">
                Resource <span className="text-red-600" data-id="element-917">*</span>
              </label>
              <select value={newEntry.resource_name} onChange={e => setNewEntry({
              ...newEntry,
              resource_name: e.target.value as ResourceName
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-918">
                <option value="" data-id="element-919">Select Resource</option>
                {RESOURCES.map(resource => <option key={resource} value={resource} data-id="element-920">
                    {resource}
                  </option>)}
              </select>
            </div>

            <div data-id="element-921">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-922">
                Hours <span className="text-red-600" data-id="element-923">*</span>
              </label>
              <input type="number" step="0.25" placeholder="0.00" value={newEntry.hours} onChange={e => setNewEntry({
              ...newEntry,
              hours: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-924" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-925">
                Rounded to nearest 0.25
              </p>
            </div>

            <div data-id="element-926">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-927">
                Date
              </label>
              <input type="date" value={newEntry.date} onChange={e => setNewEntry({
              ...newEntry,
              date: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-928" />
            </div>
          </div>

          <div data-id="element-929">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-930">
              Notes
            </label>
            <input type="text" placeholder="What did you work on?" value={newEntry.notes} onChange={e => setNewEntry({
            ...newEntry,
            notes: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-931" />
          </div>

          <div className="flex justify-end" data-id="element-932">
            <Button onClick={handleAddEntry} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-934" />} data-id="element-933">
              Log Time
            </Button>
          </div>
        </div>
      </Card>

      {/* Time Entries List */}
      <Card padding="none" data-id="element-935">
        <div className="p-4 border-b border-gray-200" data-id="element-936">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-937">Time Entries</h2>
        </div>
        {filteredEntries.length === 0 ? <div className="p-8 text-center" data-id="element-938">
            <ClockIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-939" />
            <p className="text-gray-500" data-id="element-940">
              {hasActiveFilters ? 'No time entries match your filters.' : 'No time entries yet. Log your first entry above!'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-941">
            <table className="w-full" data-id="element-942">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-943">
                <tr data-id="element-944">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-945">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-946">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-947">
                    Resource
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-948">
                    Hours
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-949">
                    Notes
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-950">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-951">
                {filteredEntries.map(entry => <tr key={entry.id} className="hover:bg-gray-50" data-id="element-952">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-953">
                      {new Date(entry.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-954">
                      <div className="flex items-center gap-2" data-id="element-955">
                        <FolderIcon className="w-4 h-4 text-gray-400" data-id="element-956" />
                        {getProjectName(entry.project_id)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-957">
                      <div className="flex items-center gap-2" data-id="element-958">
                        <UserIcon className="w-4 h-4 text-gray-400" data-id="element-959" />
                        {entry.resource_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap" data-id="element-960">
                      {entry.hours.toFixed(2)}h
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-id="element-961">
                      {entry.notes || '-'}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-962">
                      <button onClick={() => deleteTimeEntry(entry.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" title="Delete" data-id="element-963">
                        <TrashIcon className="w-4 h-4" data-id="element-964" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Summary by Resource */}
      {Object.keys(resourceTotals).length > 0 && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-965">
          <Card data-id="element-966">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-967">
              Hours by Resource
            </h2>
            <div className="space-y-3" data-id="element-968">
              {Object.entries(resourceTotals).sort((a, b) => b[1] - a[1]).map(([resource, hours]) => <div key={resource} className="flex items-center justify-between" data-id="element-969">
                    <div className="flex items-center gap-2" data-id="element-970">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-971" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-972">
                        {resource}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-973">
                      {hours.toFixed(2)}h
                    </span>
                  </div>)}
            </div>
          </Card>

          <Card data-id="element-974">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-975">
              Hours by Project
            </h2>
            <div className="space-y-3" data-id="element-976">
              {Object.entries(projectTotals).sort((a, b) => b[1] - a[1]).map(([projectId, hours]) => <div key={projectId} className="flex items-center justify-between" data-id="element-977">
                    <div className="flex items-center gap-2" data-id="element-978">
                      <FolderIcon className="w-4 h-4 text-gray-400" data-id="element-979" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-980">
                        {getProjectName(projectId)}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-981">
                      {hours.toFixed(2)}h
                    </span>
                  </div>)}
            </div>
          </Card>
        </div>}
    </div>;
}